import {
	OrderShippingMethod,
	VetterPriceUsageStatus,
	VetterProductUsageStatus,
	type GetOrderQuoteVetterJobsQuery,
} from '@codezo/graphql';
import { Check, Pencil, RefreshCw } from 'lucide-react';
import { Fragment } from 'react';
import { cn } from '../../../lib/utils';
import { CurrencyText } from '../../CurrencyText';
import type { QuoteLine } from './QuoteLinePicker';
import { Button } from '../../ui/button';

type VetterJob = GetOrderQuoteVetterJobsQuery['getOrderQuoteVetterJobs'][number];
type VetterPrice = VetterJob['prices'][number];
type ShippingPrice = VetterPrice['shippingPrices'][number];
type ProductUsage = VetterJob['productUsage'][number];

/** Already used: the action is a quiet text link, not a button competing with new prices. */
const REUSE = new Set([VetterProductUsageStatus.Used, VetterProductUsageStatus.EditedOnOffer]);

/** The action's weight: loud when the supplier moved, quiet once used, plain otherwise. */
const BUTTON_VARIANT: Record<VetterProductUsageStatus, 'default' | 'outline' | 'ghost'> = {
	[VetterProductUsageStatus.SupplierUpdated]: 'default',
	[VetterProductUsageStatus.Used]: 'ghost',
	[VetterProductUsageStatus.EditedOnOffer]: 'ghost',
	[VetterProductUsageStatus.PartlyUsed]: 'outline',
	[VetterProductUsageStatus.NotUsed]: 'outline',
};

/** How the product's usage line reads: quiet once used, a highlight when the supplier moved. */
const USAGE_TONE: Record<VetterProductUsageStatus, string> = {
	[VetterProductUsageStatus.Used]: 'text-text-4',
	[VetterProductUsageStatus.PartlyUsed]: 'text-text-3',
	[VetterProductUsageStatus.EditedOnOffer]: 'text-text-3',
	[VetterProductUsageStatus.SupplierUpdated]:
		'rounded-full bg-status-warn/10 px-2 py-0.5 text-status-warn',
	[VetterProductUsageStatus.NotUsed]: '',
};

const METHODS: { method: OrderShippingMethod; label: string }[] = [
	{ method: OrderShippingMethod.Standard, label: 'Standard' },
	{ method: OrderShippingMethod.Express, label: 'Express' },
];

const dayRange = (min?: number | null, max?: number | null) => {
	if (min == null && max == null) return '—';
	if (min == null || max == null || min === max) return `${min ?? max} d`;
	return `${min}–${max} d`;
};

/**
 * What a supplier sent back: one table per product, one row per quantity. All
 * figures are the SUPPLIER'S COST in the currency they chose — never the
 * client's price. Each product says whether the offer uses these prices, and
 * each quantity carries a small marker; the words are the backend's.
 */
export function VetterJobPrices({
	job,
	lines,
	onUse,
}: {
	job: VetterJob;
	lines: QuoteLine[];
	/** Take this product's prices into an offer; left out where they can't be used. */
	onUse?: (orderQuoteItemId: string, productName: string) => void;
}) {
	const byLine = new Map<string, VetterPrice[]>();
	for (const price of job.prices) {
		const rows = byLine.get(price.orderQuoteItemId) ?? [];
		rows.push(price);
		byLine.set(price.orderQuoteItemId, rows);
	}
	const lineName = (id: string) => lines.find((l) => l.id === id)?.name ?? 'Product';
	const usageOf = (id: string) => job.productUsage.find((u) => u.orderQuoteItemId === id);

	return (
		<div className="flex flex-col gap-3">
			{[...byLine.entries()].map(([lineId, prices]) => (
				<div key={lineId}>
					<div className="mb-1 flex items-center gap-2">
						<span className="flex min-w-0 flex-1 items-center gap-2">
							<span className="truncate text-xs font-medium text-text-2">
								{lineName(lineId)}
							</span>
							<UsageLabel usage={usageOf(lineId)} />
						</span>
						{onUse && (
							<Button
								size="sm"
								variant={
									BUTTON_VARIANT[
										usageOf(lineId)?.status ?? VetterProductUsageStatus.NotUsed
									]
								}
								className={
									REUSE.has(
										usageOf(lineId)?.status ?? VetterProductUsageStatus.NotUsed
									)
										? 'h-auto px-0 text-xs text-text-3 hover:bg-transparent hover:text-text-1'
										: undefined
								}
								onClick={() => onUse(lineId, lineName(lineId))}
							>
								{usageOf(lineId)?.actionLabel ?? 'Use these prices'}
							</Button>
						)}
					</div>
					<div className="overflow-x-auto">
						<table className="w-full text-left text-xs">
							<thead className="text-text-4">
								<tr>
									<th className="py-1 pr-3 font-normal">Quantity</th>
									<th className="py-1 pr-3 font-normal">Unit cost</th>
									<th className="py-1 pr-3 font-normal">Production</th>
									{METHODS.map((m) => (
										<th key={m.method} className="py-1 pr-3 font-normal">
											{m.label} shipping
										</th>
									))}
								</tr>
							</thead>
							<tbody className="text-text-2">
								{prices.map((p) => (
									<Fragment key={p.id}>
										<tr className="border-t border-line-1">
											<td className="py-1.5 pr-3 tabular-nums">
												<span className="inline-flex items-center gap-1.5">
													{p.quantity.toLocaleString('en-US')}
													<RowMarker usage={p.usage} />
												</span>
											</td>
											<td className="py-1.5 pr-3 tabular-nums">
												<Converted
													amount={p.converted?.baseUnitPrice}
													currency={p.converted?.currency}
													quoted={p.baseUnitPrice}
													quotedCurrency={p.currency}
													unit
												/>
											</td>
											<td className="py-1.5 pr-3 tabular-nums">
												{dayRange(p.minProductionDays, p.maxProductionDays)}
											</td>
											{METHODS.map((m) => (
												<td
													key={m.method}
													className="py-1.5 pr-3 tabular-nums"
												>
													<ShippingCell
														price={p.shippingPrices.find(
															(s) => s.shippingMethod === m.method
														)}
														converted={p.converted?.shippingPrices.find(
															(s) => s.shippingMethod === m.method
														)}
														convertedCurrency={p.converted?.currency}
														currency={p.currency}
													/>
												</td>
											))}
										</tr>
									</Fragment>
								))}
							</tbody>
						</table>
					</div>
				</div>
			))}
			{job.note && (
				<div className="rounded-[var(--r-2)] bg-surface-sunken px-3 py-2 text-xs whitespace-pre-line text-text-2">
					<span className="text-text-4">Supplier&apos;s note · </span>
					{job.note}
				</div>
			)}
		</div>
	);
}

/**
 * A supplier's figure: in the header currency first, what they quoted beneath
 * when a rate was applied. Without a conversion (no rate), theirs alone.
 */
function Converted({
	amount,
	currency,
	quoted,
	quotedCurrency,
	unit,
}: {
	amount?: number | null;
	currency?: string | null;
	quoted: number;
	quotedCurrency: string;
	unit?: boolean;
}) {
	if (amount == null || !currency || currency === quotedCurrency) {
		return <CurrencyText amount={quoted} currency={quotedCurrency} unit={unit} />;
	}
	return (
		<span className="inline-flex flex-col">
			<CurrencyText amount={amount} currency={currency} unit={unit} />
			<span className="text-2xs text-text-4">
				supplier quoted{' '}
				<CurrencyText amount={quoted} currency={quotedCurrency} unit={unit} />
			</span>
		</span>
	);
}

function ShippingCell({
	price,
	converted,
	convertedCurrency,
	currency,
}: {
	price?: ShippingPrice;
	converted?: { price: number } | null;
	convertedCurrency?: string | null;
	currency: string;
}) {
	// Not offered — the supplier left the method out.
	if (!price) return <span className="text-text-4">—</span>;
	const days =
		price.deliveryDaysMin != null || price.deliveryDaysMax != null
			? ` · ${dayRange(price.deliveryDaysMin, price.deliveryDaysMax)}`
			: '';
	return (
		<span>
			<Converted
				amount={converted?.price}
				currency={convertedCurrency}
				quoted={price.price}
				quotedCurrency={currency}
			/>
			<span className="text-text-4">{days}</span>
		</span>
	);
}

function UsageLabel({ usage }: { usage?: ProductUsage }) {
	if (!usage?.label) return null;
	return (
		<span
			className={cn(
				'inline-flex shrink-0 items-center gap-1 text-xs',
				USAGE_TONE[usage.status]
			)}
		>
			{usage.status === VetterProductUsageStatus.Used && <Check size={12} />}
			{usage.label}
		</span>
	);
}

/** A quantity's usage as a small icon; the backend's words on hover. */
function RowMarker({ usage }: { usage: VetterPrice['usage'] }) {
	if (!usage?.label) return null;
	const icon: Record<VetterPriceUsageStatus, React.ReactNode> = {
		[VetterPriceUsageStatus.Applied]: <Check size={11} className="text-status-success" />,
		[VetterPriceUsageStatus.SupplierUpdated]: (
			<RefreshCw size={11} className="text-status-warn" />
		),
		[VetterPriceUsageStatus.EditedOnOffer]: <Pencil size={11} className="text-text-4" />,
		[VetterPriceUsageStatus.NotApplied]: null,
	};
	return (
		<span title={usage.label} aria-label={usage.label} className="inline-flex">
			{icon[usage.status]}
		</span>
	);
}
